import { VehicleModel, VehicleType, Manufacturer } from '../types';
import { isDemoMode } from './environment';

/**
 * 車両モデルIDから車種名を取得する
 * @param vehicleModelId 車両モデルID
 * @param getVehicleModelById 車両モデル取得関数
 * @param getVehicleTypeById 車種取得関数
 * @returns 車種名（軽自動車、セダンなど）
 */
export function getVehicleTypeFromModel(
  vehicleModelId: string,
  getVehicleModelById: (id: string) => VehicleModel | undefined,
  getVehicleTypeById: (id: string) => VehicleType | null
): string {
  if (!vehicleModelId) return '';

  const model = getVehicleModelById(vehicleModelId);
  if (!model) return '';

  const vehicleTypeData = getVehicleTypeById(model.vehicleTypeId);
  return vehicleTypeData ? vehicleTypeData.name : '';
}

/**
 * 車両モデルの表示名をフォーマットする
 * @param vehicleModel 車両モデル
 * @param manufacturer メーカー情報（オプション）
 * @returns フォーマットされた表示名（例: "トヨタ プリウス"）
 */
export function formatVehicleDisplayName(
  vehicleModel: VehicleModel,
  manufacturer?: Manufacturer
): string {
  if (manufacturer) {
    return `${manufacturer.name} ${vehicleModel.name}`;
  }
  return vehicleModel.name;
}

/**
 * 車両データのバリデーション
 * @param vehicleModelId 車両モデルID
 * @param vehiclePlateNumber ナンバープレート番号
 * @param vehiclePlateNumberNone ナンバープレート番号なしフラグ
 * @returns バリデーション結果
 */
export function validateVehicleData(
  vehicleModelId: string,
  _vehiclePlateNumber: string,
  _vehiclePlateNumberNone?: boolean
): { isValid: boolean; error?: string } {
  // 車両モデルは必須
  if (!vehicleModelId) {
    return {
      isValid: false,
      error: '車両モデルを選択してください。',
    };
  }

  // ナンバープレート番号は任意項目（削除済み）

  return { isValid: true };
}

/**
 * 車両モデル選択時の状態更新データを作成
 * @param vehicleModelId 選択された車両モデルID
 * @param getVehicleModelById 車両モデル取得関数
 * @param getVehicleTypeById 車種取得関数
 * @returns 更新用のデータオブジェクト
 */
export function createVehicleModelUpdateData(
  vehicleModelId: string | null,
  getVehicleModelById: (id: string) => VehicleModel | undefined,
  getVehicleTypeById: (id: string) => VehicleType | null
): { vehicleModel: string; vehicleType: string } {
  const normalizedModelId = vehicleModelId || '';
  const vehicleType = getVehicleTypeFromModel(
    normalizedModelId,
    getVehicleModelById,
    getVehicleTypeById
  );

  return {
    vehicleModel: normalizedModelId,
    vehicleType,
  };
}

/**
 * 地域コード定義
 */
const REGION_CODES = [
  '品川',
  '横浜',
  '千葉',
  '足立',
  '練馬',
  '多摩',
  '杉並',
  '板橋',
  '川口',
  '川崎',
  '湘南',
  '習志野',
  '成田',
  '袖ヶ浦',
  '柏',
  '野田',
] as const;

/**
 * ひらがな一覧
 */
const HIRAGANA_CHARS = [
  'あ',
  'い',
  'う',
  'え',
  'お',
  'か',
  'き',
  'く',
  'け',
  'こ',
  'さ',
  'し',
  'す',
  'せ',
  'そ',
  'た',
  'ち',
  'つ',
  'て',
  'と',
  'な',
  'に',
  'ぬ',
  'ね',
  'の',
  'は',
  'ひ',
  'ふ',
  'へ',
  'ほ',
  'ま',
  'み',
  'む',
  'め',
  'も',
  'や',
  'ゆ',
  'よ',
  'ら',
  'り',
  'る',
  'れ',
  'ろ',
  'わ',
] as const;

/**
 * 車種別分類番号の定義
 */
const CLASSIFICATION_NUMBERS = {
  軽自動車: '500',
  普通車: '300',
  SUV: '300',
  ミニバン: '300',
  セダン: '300',
  ハッチバック: '300',
  ワゴン: '300',
  クーペ: '300',
  その他: '300',
} as const;

/**
 * ナンバープレート番号生成オプション
 */
export interface VehiclePlateNumberOptions {
  /** 既存のナンバープレート番号一覧（重複チェック用） */
  existingPlateNumbers?: string[];
  /** 車種（分類番号決定に使用） */
  vehicleType?: string;
  /** 地域コード（指定しない場合はランダム選択） */
  regionCode?: string;
}

/**
 * デモ用ナンバープレート番号を自動生成する
 * @param options 生成オプション
 * @returns 生成されたナンバープレート番号（例: "品川 300 あ 1234"）
 */
export function generateVehiclePlateNumber(options: VehiclePlateNumberOptions = {}): string {
  const { existingPlateNumbers = [], vehicleType = '普通車', regionCode } = options;

  // 地域コードの決定
  const selectedRegionCode =
    regionCode || REGION_CODES[Math.floor(Math.random() * REGION_CODES.length)];

  // 分類番号の決定
  const classificationNumber =
    CLASSIFICATION_NUMBERS[vehicleType as keyof typeof CLASSIFICATION_NUMBERS] ||
    CLASSIFICATION_NUMBERS['その他'];

  // ひらがなの選択
  const hiragana = HIRAGANA_CHARS[Math.floor(Math.random() * HIRAGANA_CHARS.length)];

  // 連番の生成（1000〜9999の範囲）
  let serialNumber: string;
  let plateNumber: string;
  let attempts = 0;
  const maxAttempts = 100;

  do {
    const randomSerial = Math.floor(Math.random() * 9000) + 1000;
    serialNumber = randomSerial.toString();
    plateNumber = `${selectedRegionCode} ${classificationNumber} ${hiragana} ${serialNumber}`;
    attempts++;
  } while (existingPlateNumbers.includes(plateNumber) && attempts < maxAttempts);

  // 最大試行回数に達した場合、現在時刻を使用してユニークにする
  if (attempts >= maxAttempts) {
    const timestamp = Date.now().toString().slice(-4);
    plateNumber = `${selectedRegionCode} ${classificationNumber} ${hiragana} ${timestamp}`;
  }

  return plateNumber;
}

/**
 * 車両モデル選択時のナンバープレート番号自動設定データを作成
 * @param vehicleModelId 選択された車両モデルID
 * @param vehicleType 車種
 * @param existingPlateNumbers 既存のナンバープレート番号一覧
 * @param currentPlateNumber 現在のナンバープレート番号（編集時）
 * @returns 更新用のデータオブジェクト
 */
export function createVehiclePlateNumberUpdateData(
  vehicleModelId: string,
  vehicleType: string,
  existingPlateNumbers: string[] = [],
  currentPlateNumber?: string
): { vehiclePlateNumber: string; shouldAutoGenerate: boolean } {
  // デモモードではない場合、自動生成しない
  if (!isDemoMode()) {
    return {
      vehiclePlateNumber: currentPlateNumber || '',
      shouldAutoGenerate: false,
    };
  }

  // 既にナンバープレート番号が設定されている場合（編集時）、変更しない
  if (currentPlateNumber && currentPlateNumber.trim() !== '') {
    return {
      vehiclePlateNumber: currentPlateNumber,
      shouldAutoGenerate: false,
    };
  }

  // 車両モデルが選択されていない場合、ナンバープレート番号をクリア
  if (!vehicleModelId || !vehicleType) {
    return {
      vehiclePlateNumber: '',
      shouldAutoGenerate: false,
    };
  }

  // 新しいナンバープレート番号を生成
  const generatedPlateNumber = generateVehiclePlateNumber({
    existingPlateNumbers,
    vehicleType,
  });

  return {
    vehiclePlateNumber: generatedPlateNumber,
    shouldAutoGenerate: true,
  };
}

/**
 * 車体番号（VIN）の末尾4桁を取得
 * @param vehicleNumber 車体番号（17桁のVIN）
 * @returns 末尾4桁（例: "5555"）、データがない場合は空文字
 */
export function getVehicleNumberLast4Digits(vehicleNumber: string | undefined | null): string {
  if (!vehicleNumber || vehicleNumber.trim().length < 4) {
    return '';
  }
  return vehicleNumber.slice(-4);
}

/**
 * 車体番号（VIN）の末尾4桁を表示用にフォーマット
 * @param vehicleNumber 車体番号（17桁のVIN）
 * @returns フォーマット済み文字列（例: "末尾: 5555"）
 */
export function formatVehicleNumberForDisplay(vehicleNumber: string | undefined | null): string {
  const last4 = getVehicleNumberLast4Digits(vehicleNumber);
  return last4 ? `末尾: ${last4}` : '';
}

/**
 * FL-191: 車体番号（日本型式）の末尾4桁を取得
 * @param chassisNumber 車体番号（例: "JZS171-0123456"）
 * @returns 末尾4桁（例: "3456"）、データがない場合は空文字
 */
export function getChassisNumberLast4Digits(chassisNumber: string | undefined | null): string {
  if (!chassisNumber || chassisNumber.trim().length < 4) {
    return '';
  }
  return chassisNumber.slice(-4);
}

/**
 * FL-191: 車体番号（日本型式）の末尾4桁を表示用にフォーマット
 * @param chassisNumber 車体番号（例: "JZS171-0123456"）
 * @returns フォーマット済み文字列（例: "末尾: 3456"）
 */
export function formatChassisNumberForDisplay(chassisNumber: string | undefined | null): string {
  const last4 = getChassisNumberLast4Digits(chassisNumber);
  return last4 ? `末尾: ${last4}` : '';
}
